import RadioGroupBase from '@base/components/src/RadioGroupBase';
import { ProductServicesElement } from '@package/core/src/shoppingCart/types';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';

import ServiceRadio from '../ProductService/ServiceRadio';

import styles from './ProductServiceContent.module.css';
import {
  DefaultOptionValue,
  ProductServiceContentProps,
  ServiceOptionProps,
} from './types';
import ProductServiceCheckbox from '../ProductServiceCheckbox';

const ProductServiceContent = (props: Readonly<ProductServiceContentProps>) => {
  const {
    title,
    productSku,
    services,
    addProductService,
    deleteProductService,
    className,
    combineAllowed,
    deleteSpecifiedService,
  } = props;
  const [selectedServices, setSelectedServices] = useState<DefaultOptionValue>(null);

  const disableServiceRadio = selectedServices !== null;

  const stopLoading = useCallback(() => {
    setSelectedServices(null);
  }, []);

  const handleAddProduct = useCallback(async (service: ProductServicesElement) => {
    setSelectedServices(service.price);
    try {
      setSelectedServices(service.price);
      await addProductService({ ...service });
    } catch (error) {
      stopLoading();
    }
  }, []);

  const mappedOptions = useMemo(() => {
    const isSelectedOption = services.some((service) => service.selected);
    const newOptions = services.map((service) => ({
      label: service.name,
      value: service.price,
      checked: service.selected,
      canSelect: service.canAddToCart,
      onChange: async () => {
        if (service.selected) return;
        await handleAddProduct(service);
      },
      optionProps: {
        id: service.id,
        canAddToCart: service.canAddToCart,
        hideInfoIcon: false,
        addService: async () => await handleAddProduct(service),
      },
    }));

    if (isSelectedOption) {
      newOptions.push({
        label: 'None',
        value: 0,
        checked: false,
        canSelect: true,
        onChange: async () => {
          setSelectedServices(0);
          await deleteProductService();
        },
        optionProps: {
          id: 0,
          canAddToCart: true,
          hideInfoIcon: true,
          addService: async () => await deleteProductService(),
        },
      });
    }
    return newOptions;
  }, [services]);

  return (
    <div className={clsx(styles.contentContainer, className)}>
      <p className={styles.title}>{title}</p>
      {!combineAllowed ? (
        <RadioGroupBase<ServiceOptionProps>
          name="serviceRadio"
          options={mappedOptions}
          classNameRoot={clsx(styles.radioGroup, styles.mobileView)}
          layout="vertical"
          renderRadio={({ optionProps, ...rest }) =>
            optionProps ? (
              <ServiceRadio
                {...rest}
                key={optionProps.id}
                hideInfoIcon={optionProps.hideInfoIcon}
                serviceId={optionProps.id}
                canAddToCart={optionProps.canAddToCart}
                productSku={productSku}
                disabled={disableServiceRadio}
                selectedService={selectedServices}
                stopLoading={stopLoading}
                addService={optionProps.addService}
                className={styles.radio}
                data-testid="service-radio-button"
              />
            ) : null
          }
        />
      ) : (
        <ProductServiceCheckbox
          deleteService={deleteSpecifiedService}
          addProductService={addProductService}
          services={services}
          productSku={productSku}
        />
      )}
    </div>
  );
};

export default ProductServiceContent;
